import { MessageBarIntent } from '@fluentui/react-components';
import { UserPermissions } from './permissionTypes';

export interface Lifecycle {
    createdOn?: Date;
    createdBy?: string;
    modifiedOn?: Date;
    modifiedBy?: string;
    deletedOn?: Date;
    deletedBy?: string;
    isDeleted?: boolean;
}

export interface HasLifecycle {
    lifecycle?: Lifecycle;
}
export interface HasTaskLifecycle {
    lifecycle?: TaskLifecycle;
}
export interface IsDeletable {
    isDeleted?: boolean;
    isPurged?: boolean;
}

export interface TaskLifecycle extends Lifecycle {
    completedOn?: Date;
    completedBy?: string;
    closedOn?: Date;
    closedBy?: string;
}

export interface Address extends HasLifecycle {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
}

export interface Phone extends HasLifecycle {
    number: string;
    type: 'office' | 'cell' | 'other' | string | undefined;
    primary: boolean;
}

export interface Individual extends HasLifecycle {
    accountId?: string;
    name?: string;
    title?: string;
    address?: Address;
    email?: string;
}

export interface Contact extends Individual {
    contactId?: string;
    partyId?: string;
    phone?: Phone[];
}

export interface User extends Individual {
    username: string;
    userId: string;
    permissions?: UserPermissions;
    contractor?: boolean;
    expectedHours?: number;
    recentCases?: CaseInfo[];
    phone?: Phone;
    acceptedTOS?: Date;
}

export interface UserRef {
    userId: string;
    username: string;
    name?: string;
    hourlyRate?: string;
    clientBillRate?: string;
    userPayRate?: string;
    expectedHours?: number;
}

export interface ClientPortal extends HasLifecycle {
    portalId?: string;
    accountId?: string;
    caseId?: string;
    isEnabled: boolean;
}

export interface ClientPortalUser extends HasLifecycle {
    portalId?: string;
    userId?: string;
    username?: string;
    name?: string;
    email?: string;
}

export type Person = Contact | User | UserRef;

export interface Party extends HasLifecycle, IsDeletable {
    partyId?: string;
    accountId?: string;
    name?: string;
    address?: Address;
    phone?: Phone[];
    url?: string;
    logoUrl?: string;
    partyContacts?: Contact[];
}

export interface PartyWithContacts {
    party?: Party;
    contacts: Contact[];
}

export interface CaseEvent extends HasLifecycle, IsDeletable {
    accountId?: string;
    caseId?: string;
    eventId?: string;
    event: string;
    description?: string;
    location?: string;
    eventType?: string;
    date?: Date;
    startDateTime?: Date;
    endDateTime?: Date;
    due?: boolean;
    allDay?: boolean;
    ownerId?: string;
    priority?: string;
    milestoneId?: string;
    followUpRequired?: boolean;
    billable?: boolean;
    hasNotes?: boolean;
}

export interface UpcomingEvent extends CaseEvent {
    caseName?: string;
}

export interface Note extends HasLifecycle, IsDeletable {
    accountId?: string;
    caseId?: string;
    noteId?: string;
    text: string;
    taskId?: string;
    eventId?: string;
    documentId?: string;
    milestoneId?: string;
}

export interface CaseInfo extends HasLifecycle {
    accountId?: string;
    caseId?: string;
    caseName?: string;
}

export interface Case extends CaseInfo, IsDeletable {
    caseType?: string; //pull from saved editable picklist
    shortDescription?: FieldEntry;
    retainedDate?: Date;
    retainedBy?: PartyWithContacts;
    overview?: string;
    isFixedFee?: boolean;
    fixedAmount?: string;
    estimatedHours?: string;
    affiliatedParties?: PartyWithContacts[];
    caseEvents?: CaseEvent[];
    caseNotes?: Note[];
    caseLead?: User;
    assignedUsers?: UserRef[];
    status?: CaseStatus;
    statusChanges?: CaseStatusChange[];
    allowedStatuses?: CaseStatus[];
}

export interface CaseStatus {
    status: string;
    systemStatus: CaseSystemStatus;
    description?: string;
    order?: number;
}

export interface CaseStatusChange {
    caseStatus: CaseStatus;
    statusDate: Date;
}

export enum CaseSystemStatus {
    new = 'New',
    active = 'Active',
    completed = 'Completed',
    closed = 'Closed',
    removed = 'Removed',
}
export enum CaseSystemStatusOrder {
    new = 1,
    active = 2,
    completed = 3,
    closed = 4,
    removed = 5,
}

export interface CaseData {
    accountId: string;
    types: string[];
    statuses: CaseStatus[];
    expenseTypes: string[];
}

export type ControlSize =
    | 'icon'
    | 'small-option'
    | 'option'
    | 'small'
    | 'smallCard'
    | 'medium'
    | 'mediumCard'
    | 'large'
    | undefined;

export interface CaseDocument extends HasLifecycle, IsDeletable {
    accountId?: string;
    caseId?: string;
    docId?: string;
    name?: string;
    description?: string;
    summary?: string;
    size?: number;
    uploaded?: boolean;
    hasNotes?: boolean;
    milestoneId?: string;
    taskId?: string;
    eventId?: string;   
    isExpenseAttachment?: boolean;
}

export enum TaskStatus {
    new = 'New',
    open = 'Open',
    completed = 'Completed',
    closed = 'Closed',
}
export enum TaskStatusOrder {
    new = 0,
    open = 1,
    completed = 2,
    closed = 3,
}

export interface Task extends HasTaskLifecycle, IsDeletable {
    taskId?: string;
    accountId?: string;
    caseId?: string;
    userId?: string;
    username?: string;

    title?: string;
    text?: string;
    dueBy?: Date;
    category?: string;
    status?: TaskStatus;
    estimatedHours?: string;
    billable?: boolean;
    hasNotes?: boolean;
    milestoneId?: string;
    sortKey?: number;
    results?: string;
}

export interface DocumentReference {
    caseId?: string;
    documentId?: string;
}

export interface SignalRConnectionInfo {
    url: string;
    accessToken: string;
}

export interface TimeTrackingEntry extends HasLifecycle, IsDeletable {
    accountId?: string;
    caseId?: string;
    username?: string;
    userId?: string;
    entryId?: string;
    taskId?: string;
    date?: Date;
    description?: string;
    hours: number;
    timerOn?: boolean;
    startTime?: Date;
    isBillable?: boolean;
    rate?: number;
    invoiceId?: string;
}

export interface SecureUploadLink {
    accountId?: string;
    caseId?: string;
    linkId?: string;
    token?: string;
}

export interface CaseExpense extends IsDeletable {
    accountId?: string;
    caseId?: string;
    expenseId?: string;
    username?: string;
    text?: string;
    date?: Date;
    amount?: string;
    type?: string;
    attachmentId?: string;
    attachmentName?: string;
    invoiceId?: string;
    recoverable?: boolean;
}

export interface CaseEngagementDoc extends HasLifecycle {
    accountId?: string;
    caseId?: string;
    engagementDocId?: string;
    isAddendum: boolean;
    isExecuted: boolean;
    title?: string;
    paymentBy?: string;
    addSignatures?: boolean;
    fields?: FieldEntry[];
    accountSignatures?: UserRef[];
    clientSignatures?: Contact[];
}

export interface Invoice extends IsDeletable {
    accountId?: string;
    caseId?: string;
    invoiceId?: string;
    invoiceNumber?: string;
    invoiceDate?: Date;
    billingPeriodStart?: Date;
    billingPeriodEnd?: Date;
    dueDate?: Date;
    billableTimeEntries?: TimeTrackingEntry[];
    expenses?: CaseExpense[];
    fixedAmount?: string;
    attachEngagement?: boolean;
    paymentBy?: string;
    isPaid?: boolean;
    paidDate?: Date;
    credits?: Credit[];
}

export interface AppMessage {
    id: string;
    intent: MessageBarIntent;
    title?: string;
    content?: string | JSX.Element;
    actions?: JSX.Element;
    canDismiss?: boolean;
}

export interface EngagementTemplate extends HasLifecycle, IsDeletable {
    accountId?: string;
    templateId?: string;
    templateName?: string;
    title?: string;
    description?: string;
    addSignatures?: boolean;
    fields?: FieldEntry[];
}

export interface FieldEntry {
    fieldName?: string;
    fieldValue?: string;
}

export interface CaseEventTemplate {
    id?: string;
    eventName?: string;
    description?: string;
    daysFromRetained?: number;
    due?: boolean;
}

export interface CaseTemplate extends IsDeletable {
    accountId?: string;
    templateId?: string;
    templateName?: string;
    templateDescription?: string;
    caseType?: string;
    statuses?: CaseStatus[];
    shortDescription?: FieldEntry;
    overview?: string;
    events?: CaseEventTemplate[];
    milestones?: CaseMilestoneTemplate[];
    notes?: string[];
    engagementTemplate?: EngagementTemplate;
    lifecycle?: Lifecycle;
}

export interface CaseMilestone extends HasLifecycle, IsDeletable {
    accountId?: string;
    caseId?: string;
    milestoneId?: string;
    name?: string;
    description?: string;
    deliverables?: string;
    dueDate?: Date;
    deliverablesDelivered?: boolean;
    completed?: boolean;
    completedOn?: Date;
    completedBy?: string;
    sortKey?: number;
    escalationDate?: Date;
    category?: string;
    priority?: number;
    responsibleUser?: string;
    secondaryReviewer?: string;
    externalDependencies?: string;
    blocked?: boolean;
    currentBlockers?: string;
    blockerOwner?: string;
    definitionOfDone?: string;
    approvalStatus?: string;
    approvedBy?: string;
    resultSummary?: string;
    linkedDocuments?: string[];
    externalLinks?: string[];
    estimatedHoursCost?: string;
    actualHoursCost?: string;
    budgetVarianceReason?: string;
    lessonsLearned?: string;
}
export const MILESTONE_SORT_KEY_INCREMENT = 100;

export interface CaseMilestoneTemplate {
    id?: string;
    name?: string;
    description?: string;
    deliverables?: string;
    dueDaysFromCreated?: number;
    events?: CaseEventTemplate[];
    tasks?: TaskTemplate[];
}

export interface TaskTemplate {
    id?: string;
    username?: string;
    userId?: string;
    title?: string;
    text?: string;
    billable?: boolean;
}

export interface Notification extends IsDeletable {
    notificationId?: string;
    accountId?: string;
    userId?: string;
    type?: 'Complete' | 'Info' | 'Warning' | 'Danger' | string;
    title?: string;
    text?: string;
    link?: string;
    createdOn?: Date;
    viewedOn?: Date;
}

export interface AuditItem {
    accountId?: string;
    caseId?: string;
    itemId?: string;

    timestamp?: Date;

    actor?: AuditActor;
    action?: string;
    detail?: AuditDetail;

    ipAddress?: string;
    browser?: string;
}

export interface AuditActor {
    userId?: string;
    role?: string;
}

export interface AuditDetail {
    objectType?: string;
    id?: string;
    description?: string;
}

export interface Credit {
    creditId?: string;
    caseId?: string;
    description?: string;
    amount: number;
    dateIssued?: string;
    issuedBy?: string;
}

export interface RedeemedCredits {
    redemptionId?: string;
    caseId?: string;
    invoiceId?: string;
    description?: string;
    amount: number;
    dateRedeemed?: string;
}

export interface Credits {
    accountId?: string;
    partyId: string;
    creditsList: Credit[];
    redeemedCreditsList: RedeemedCredits[];
}